import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";

import { BasicForm, Can, EmptyMessage } from "@components/shared";
import { notifyUserFacingError } from "@util/toErrorMessage";
import { PERMISSIONS } from "@util/permissions";
import { fetchOrgUsers, orgJoinSelectLabel } from "./organizationMemberShared";

export default function OrganizationMyTeam({ organizationId }) {
  const { t } = useTranslation();
  const [links, setLinks] = useState([]);
  const [managedUsers, setManagedUsers] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [selectedOrgUserId, setSelectedOrgUserId] = useState(null);
  const [createFormKey, setCreateFormKey] = useState(0);
  const [linkEmailFormKey, setLinkEmailFormKey] = useState(0);

  useEffect(() => {
    fetchTeam();
  }, [organizationId]);

  function fetchTeam() {
    setLoading(true);
    Promise.all([
      axios.post(
        `/organizations/${organizationId}/retrieve-user-management-links/`,
        { page: 0, page_size: 200 }
      ),
      axios.post(`/organizations/${organizationId}/retrieve-users/`, {
        page: 0,
        page_size: 200,
        scope: "my_users",
      }),
      fetchOrgUsers(organizationId).catch(() => []),
    ])
      .then(([linksRes, managedRes, users]) => {
        setLinks(linksRes.data.response[0]?.data || []);
        setManagedUsers(managedRes.data.response[0]?.data || []);
        setOrgUsers(users || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const managedByOrgUserId = useMemo(() => {
    const map = {};
    (managedUsers || []).forEach((row) => {
      map[String(row.organization_user_id)] = row;
    });
    return map;
  }, [managedUsers]);

  const linkedOrgUserIds = useMemo(
    () =>
      new Set((links || []).map((link) => String(link.organization_user_id))),
    [links]
  );

  const linkableOptions = useMemo(
    () =>
      (orgUsers || [])
        .filter(
          (row) =>
            row.organization_user_id &&
            !linkedOrgUserIds.has(String(row.organization_user_id))
        )
        .map((row) => ({
          value: String(row.organization_user_id),
          label: orgJoinSelectLabel(row, row.user_id),
        })),
    [orgUsers, linkedOrgUserIds]
  );

  function createLink(body) {
    return axios.post(
      `/organizations/${organizationId}/user-management-links/`,
      body
    );
  }

  function createAndLinkUser(formData) {
    setBusyKey("create");
    axios
      .post(`/organizations/${organizationId}/create-user/`, formData)
      .then(() => {
        setBusyKey(null);
        setCreateFormKey((key) => key + 1);
        toast.success(t("created"));
        fetchTeam();
      })
      .catch((err) => {
        setBusyKey(null);
        notifyUserFacingError(err, toast, { source: "my_team_create" });
      });
  }

  function linkByOrgUserId() {
    if (!selectedOrgUserId) return;
    setBusyKey(`link-${selectedOrgUserId}`);
    createLink({ organization_user_id: selectedOrgUserId })
      .then(() => {
        setBusyKey(null);
        setSelectedOrgUserId(null);
        toast.success(t("updated"));
        fetchTeam();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  function linkByEmail(formData) {
    setBusyKey("link-email");
    axios
      .post(`/users/find-by-email/`, { email: formData.email })
      .then(({ data }) => {
        const user = data.response[0];
        if (!user?.id) {
          throw t("invite_user_not_found");
        }
        return createLink({ user_id: user.id });
      })
      .then(() => {
        setBusyKey(null);
        setLinkEmailFormKey((key) => key + 1);
        toast.success(t("updated"));
        fetchTeam();
      })
      .catch((err) => {
        setBusyKey(null);
        notifyUserFacingError(err, toast, { source: "my_team_link_email" });
      });
  }

  function unlink(linkId) {
    setBusyKey(`unlink-${linkId}`);
    axios
      .delete(
        `/organizations/${organizationId}/user-management-links/${linkId}/`
      )
      .then(() => {
        setBusyKey(null);
        toast.success(t("updated"));
        fetchTeam();
      })
      .catch((err) => {
        setBusyKey(null);
        toast.error(err);
      });
  }

  const createFields = [
    {
      name: "first_name",
      label: t("first_name"),
      initialValue: "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "last_name",
      label: t("last_name"),
      initialValue: "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "email",
      label: t("email"),
      initialValue: "",
      required: true,
      schema: () =>
        Yup.string()
          .email(t("validation:email_invalid"))
          .required(t("validation:required")),
    },
  ];

  const emailFields = [
    {
      name: "email",
      label: t("email"),
      initialValue: "",
      required: true,
      schema: () =>
        Yup.string()
          .email(t("validation:email_invalid"))
          .required(t("validation:required")),
    },
  ];

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Can permission={PERMISSIONS.ORGANIZATION_MANAGED_USERS_VIEW}>
      <Stack gap="lg">
        <Title order={4}>{t("my_team")}</Title>

        <Can permission={PERMISSIONS.ORGANIZATION_USERS_MANAGE}>
          <Card>
            <Title order={5} mb="sm">
              {t("create")} {t("user_singular")}
            </Title>
            <Text size="sm" c="dimmed" mb="sm">
              {t("my_team_create_description")}
            </Text>
            <BasicForm
              key={`create-${createFormKey}`}
              loading={busyKey === "create"}
              onSubmit={createAndLinkUser}
              fields={createFields}
              buttonProps={{ fullWidth: true }}
            />
          </Card>
        </Can>

        <Can permission={PERMISSIONS.ORGANIZATION_MANAGED_USERS_MANAGE}>
          <Card>
            <Title order={5} mb="sm">
              {t("my_team_link_existing")}
            </Title>
            {linkableOptions.length > 0 && (
              <Flex gap="sm" align="flex-end" mb="md">
                <Select
                  style={{ flex: 1 }}
                  searchable
                  clearable
                  placeholder={t("user_singular")}
                  data={linkableOptions}
                  value={selectedOrgUserId}
                  onChange={setSelectedOrgUserId}
                />
                <Button
                  leftSection={<IconPlus size={14} />}
                  disabled={!selectedOrgUserId}
                  loading={busyKey === `link-${selectedOrgUserId}`}
                  onClick={linkByOrgUserId}
                >
                  {t("add")}
                </Button>
              </Flex>
            )}
            <Text size="sm" c="dimmed" mb="sm">
              {t("my_team_link_by_email_description")}
            </Text>
            <BasicForm
              key={`link-email-${linkEmailFormKey}`}
              loading={busyKey === "link-email"}
              onSubmit={linkByEmail}
              fields={emailFields}
              buttonProps={{ fullWidth: true }}
            />
          </Card>
        </Can>

        <Stack gap="xs">
          <Text fw={700}>
            {t("my_team")} ({links.length})
          </Text>
          {!links.length && <EmptyMessage />}
          {links.map((link) => {
            const linkId = link.organization_user_management_link_id || link.id;
            const managed = managedByOrgUserId[String(link.organization_user_id)];
            const name = `${
              managed?.first_name || link.managed_first_name || ""
            } ${managed?.last_name || link.managed_last_name || ""}`.trim();
            const email = managed?.email || link.managed_email;
            const userId = managed?.user_id;

            return (
              <Card key={linkId}>
                <Flex gap="sm" align="center">
                  <div style={{ flexGrow: 1 }}>
                    {userId ? (
                      <Text
                        fw={700}
                        component={Link}
                        to={`/organizations/${organizationId}/users/${userId}`}
                        c="dark"
                        style={{ textDecoration: "none" }}
                      >
                        {name || email || link.organization_user_id}
                      </Text>
                    ) : (
                      <Text fw={700}>
                        {name || email || link.organization_user_id}
                      </Text>
                    )}
                    {email && (
                      <Text size="sm" c="dimmed">
                        {email}
                      </Text>
                    )}
                  </div>
                  <Can permission={PERMISSIONS.ORGANIZATION_MANAGED_USERS_MANAGE}>
                    <ActionIcon
                      size="sm"
                      radius="xl"
                      variant="light"
                      color="red"
                      title={t("remove")}
                      loading={busyKey === `unlink-${linkId}`}
                      onClick={() => unlink(linkId)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Can>
                </Flex>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </Can>
  );
}
